export function getQuestion(results, questionId) {
  return results?.questions?.find((question) => question.question_id === questionId) || null;
}

export function stripQuestionNumber(text = "") {
  return String(text).replace(/^\s*\d+\.\s*/, "");
}

export function buildChoiceChart(question, selectedIssues, issueOptions, mode) {
  if (!question) {
    return { categories: [], series: [] };
  }

  const categories = question.options.map((option) => option.label);
  const issues = selectedIssues || [];
  const issueLabels = new Map(
    (issueOptions || []).map((issue) => [issue.value, issue.label])
  );

  if (issues.length > 1) {
    return {
      categories,
      series: issues.map((issue) => ({
        name: issueLabels.get(issue) || issue,
        data: question.options.map((option) => {
          const value = option.by_issue?.find((item) => item.issue === issue);
          return mode === "rel" ? Number(value?.percentage) || 0 : Number(value?.count) || 0;
        }),
      })),
    };
  }

  return {
    categories,
    series: [
      {
        name: mode === "rel" ? "Percentage" : "Aantal",
        data: question.options.map((option) =>
          mode === "rel" ? Number(option.percentage) || 0 : Number(option.count) || 0
        ),
      },
    ],
  };
}

export function buildMultiChoicePeriodChart(question, filters, mode) {
  if (!question) {
    return { categories: [], series: [], details: [] };
  }

  const issues = filters?.selected_issues || [];
  const issueLabels = new Map(
    (filters?.available_issues || []).map((issue) => [issue.value, issue.label])
  );
  const details = issues.map((issue) =>
    question.options.map((option) => {
      const value = option.by_issue?.find((item) => item.issue === issue);
      return {
        count: Number(value?.count) || 0,
        percentage: Number(value?.percentage) || 0,
      };
    })
  );

  return {
    categories: question.options.map((option) => option.label),
    series: issues.map((issue, seriesIndex) => ({
      name: issueLabels.get(issue) || issue,
      data: details[seriesIndex].map((item) =>
        mode === "rel" ? item.percentage : item.count
      ),
    })),
    details,
  };
}

export function groupTextResponses(items = [], availableIssues = []) {
  const issueOrder = new Map(
    availableIssues.map((issue, index) => [issue.value, index])
  );
  const groups = new Map();

  items.forEach((item) => {
    const match = /^(\d{4})-Q([1-4])$/.exec(item.issue || "");
    if (!match) {
      return;
    }
    const year = Number(match[1]);
    const quarter = Number(match[2]);
    if (!groups.has(year)) {
      groups.set(year, new Map());
    }
    const periods = groups.get(year);
    if (!periods.has(item.issue)) {
      periods.set(item.issue, { issue: item.issue, quarter, items: [] });
    }
    periods.get(item.issue).items.push({
      text: item.text,
      count: Number(item.count) || 0,
    });
  });

  return Array.from(groups.entries())
    .sort(([yearA], [yearB]) => yearA - yearB)
    .map(([year, periods]) => ({
      year,
      periods: Array.from(periods.values()).sort(
        (a, b) =>
          (issueOrder.get(a.issue) ?? Number.MAX_SAFE_INTEGER) -
          (issueOrder.get(b.issue) ?? Number.MAX_SAFE_INTEGER)
      ),
    }));
}

export function selectedIssueOptions(filters) {
  const selected = new Set(filters?.selected_issues || []);
  return (filters?.available_issues || []).filter((issue) => selected.has(issue.value));
}

export function buildLikertPeriodChart(questions = [], filters = {}) {
  const issues = filters?.selected_issues || [];
  const issueLabels = new Map(
    (filters?.available_issues || []).map((issue) => [issue.value, issue.label])
  );
  const canonicalOptions = questions[0]?.options || [];
  const rows = questions.flatMap((question) =>
    issues.map((issue, issueIndex) => {
      const optionsByCode = new Map(
        (question.options || []).map((option) => [String(option.code), option])
      );
      return {
        category:
          issueIndex === 0
            ? [stripQuestionNumber(question.text), issueLabels.get(issue) || issue]
            : issueLabels.get(issue) || issue,
        values: canonicalOptions.map((canonicalOption) => {
          const option = optionsByCode.get(String(canonicalOption.code));
          const value = option?.by_issue?.find((item) => item.issue === issue);
          return {
            count: Number(value?.count) || 0,
            percentage: Number(value?.percentage) || 0,
          };
        }),
      };
    })
  );

  return {
    categories: rows.map((row) => row.category),
    series: canonicalOptions.map((option, seriesIndex) => ({
      name: option.label,
      data: rows.map((row) => row.values[seriesIndex].percentage),
    })),
    details: canonicalOptions.map((_option, seriesIndex) =>
      rows.map((row) => row.values[seriesIndex])
    ),
  };
}
